import React from "react";
import { Box2, Vector3 } from "three";
import { CoordinateSystem } from "./CoordinateSystem";
import {
  getWarnungWichtigkeitColor,
  Warnung,
  WarnungZylinderSektor,
  WARNUNG_WICHTIGKEIT_DEFAULT,
} from "../../arrangement/Warnung";

function getWarnungTextColor(warnung: Warnung): string {
  const wichtigkeit = warnung.wichtigkeit ?? WARNUNG_WICHTIGKEIT_DEFAULT;
  return getWarnungWichtigkeitColor(wichtigkeit).text;
}

function hexToRgba(hex: string, alpha: number): string {
  const value = hex.replace("#", "");
  const normalized =
    value.length === 3
      ? value
          .split("")
          .map((char) => char + char)
          .join("")
      : value;

  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return `rgba(235, 87, 87, ${alpha})`;
  }

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function normalizeAngle(angle: number): number {
  let a = angle % (Math.PI * 2);
  if (a < 0) {
    a += Math.PI * 2;
  }
  return a;
}

function getSweep(startAngle: number, endAngle: number): number {
  const start = normalizeAngle(startAngle);
  const end = normalizeAngle(endAngle);
  let sweep = end - start;
  if (sweep < 0) {
    sweep += Math.PI * 2;
  }
  return Math.max(0.0001, sweep);
}

function isWarnungZylinderSektor(value: any): value is WarnungZylinderSektor {
  return value?.type === "zylindersektor";
}

function getSektorPathD(
  cs: CoordinateSystem,
  warnung: Warnung,
  sektor: WarnungZylinderSektor
): string {
  const start = normalizeAngle(sektor.startAngle);
  const sweep = getSweep(sektor.startAngle, sektor.endAngle);
  const segments = Math.max(12, Math.ceil((sweep / (Math.PI * 2)) * 72));

  const pointAt = (radius: number, angle: number) => {
    const worldPoint = warnung.localToWorld(
      new Vector3(
        sektor.pivot.x + radius * Math.cos(angle),
        sektor.pivot.y,
        sektor.pivot.z + radius * Math.sin(angle)
      )
    );
    return cs.pointToScreen(worldPoint);
  };

  const outerPoints = Array.from({ length: segments + 1 }, (_, i) => {
    const t = i / segments;
    return pointAt(sektor.radiusOuter, start + sweep * t);
  });

  const innerRadius = Math.max(0, sektor.radiusInner);
  const innerPoints =
    innerRadius < 0.000001
      ? [cs.pointToScreen(warnung.localToWorld(sektor.pivot.clone()))]
      : Array.from({ length: segments + 1 }, (_, i) => {
          const t = i / segments;
          return pointAt(innerRadius, start + sweep * (1 - t));
        });

  const points = [...outerPoints, ...innerPoints];
  if (points.length === 0) {
    return "";
  }

  return [
    `M ${points[0].x} ${points[0].y}`,
    ...points.slice(1).map((p) => `L ${p.x} ${p.y}`),
    "Z",
  ].join(" ");
}

export function WarnungSvg({
  cs,
  warnung,
  mode,
}: {
  cs: CoordinateSystem;
  warnung: Warnung;
  mode: "SVG-zindex-2" | "HTML";
}) {
  const box = warnung.getBox();
  const [a, b] = [box.min, box.max].map((v) => cs.pointToScreen(warnung.localToWorld(v.clone())));
  const textColor = getWarnungTextColor(warnung);
  const boxColor = hexToRgba(textColor, 0.35);

  if (mode == "HTML") {
    const center = new Box2(a, b).grid(0.5, 1);
    return (
      <div
        style={{ left: center.x + "px", top: center.y + "px", color: textColor }}
        className="warnung"
      >
        {warnung.text}
      </div>
    );
  } else {
    if (isWarnungZylinderSektor(warnung.box3OrOBB)) {
      const d = getSektorPathD(cs, warnung, warnung.box3OrOBB);
      if (d) {
        return <path d={d} className="svg-warnung-rect" style={{ fill: boxColor }} />;
      }
    }

    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const width = Math.abs(a.x - b.x);
    const height = Math.abs(a.y - b.y);
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        className="svg-warnung-rect"
        style={{ fill: boxColor }}
      />
    );
  }
}
